import type { Company as UsersServiceCompany } from '@/actions/UsersService';
import type { CompanyEntityData } from '@/components/ui/design-system/entities/company';
import type { ContactEntityData } from '@/components/ui/design-system/entities/contact';
import type { JobEntityData } from '@/components/ui/design-system/entities/job';
import { toContactEntityData } from '@/components/ui/design-system/entities/contact';
import type { ReportJob } from '@/types/report-types';
import type { UserProfile } from '@/actions/ContactsLookup';
import { profileToContact } from '@/lib/contacts/profile-to-contact';
import type { ClassifiedQuery } from '@/app/d/search/types';
import type { SourceAnalysis } from '@/lib/report-generation/steps/analyze-sources';

function isSlugLike(str: string): boolean {
  return /^[a-z0-9]+(-[a-z0-9]+)+$/.test(str);
}

function humanizeSlug(slug: string): string {
  return slug.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function resolveCompanyName(company: UsersServiceCompany): string {
  const candidates = [company.name, company.company_name].filter(Boolean) as string[];
  const displayName = candidates.find((c) => !isSlugLike(c));
  if (displayName) return displayName;
  if (company.universal_name) return humanizeSlug(company.universal_name);
  return candidates[0] || '';
}

export const mapUsersServiceCompanyToEntity = (
  company: UsersServiceCompany,
): CompanyEntityData => {
  const name = resolveCompanyName(company);
  const location =
    company.locality ||
    company.region ||
    company.country_name ||
    company.country_iso ||
    undefined;
  const website = company.website || company.domain || undefined;
  return {
    id: String(company.id),
    name,
    logoUrl: company.square_logo_url ?? undefined,
    domain: company.domain ?? undefined,
    industry: company.industry ?? undefined,
    employeeCount: company.employee_count ?? undefined,
    location: location ?? undefined,
    description: company.description ?? undefined,
    website: website ?? undefined,
    isExactMatch: company.isExactMatch,
    raw: company as unknown as Record<string, unknown>,
  };
};

export const mapReportJobToEntity = (job: ReportJob): JobEntityData => {
  const locationString = job.location ?? '';
  const parts = locationString.split(',').map((part) => part.trim()).filter(Boolean);
  const city = parts[0] ?? '';
  const state = parts[1] ?? '';
  const country = parts[2];
  return {
    id: job.id,
    title: job.title,
    company: {
      id: job.companyId,
      name: job.company ?? '',
    },
    locations: [
      {
        city,
        state,
        country,
        remote: job.remote,
        key: `${city} ${state}`.trim().toLowerCase() || (job.remote ? 'remote' : job.id),
      },
    ],
    postedAt: job.postedDate,
    description: job.description,
    externalUrl: job.link,
    fitReason: job.matchReason,
  };
};

/**
 * Map a raw users-service / MixRank profile (the shape returned by
 * `queryUsers`) into a `ContactEntityData`. Goes through the shared
 * `profileToContact` so the unified-search pipeline produces exactly the same
 * contact shape as the client-side company-contact loaders — previously this
 * cast the profile straight to `ReportContact`, leaving every contact with
 * `id`/`fullName`/`email`/`company` undefined (which the Simple Search deck
 * then dropped on its `!contact.id` filter, surfacing "No contacts to show").
 */
export const mapContactRecordToEntity = (
  record: Record<string, unknown>,
): ContactEntityData =>
  toContactEntityData(profileToContact(record as unknown as UserProfile));

export const mapAnalysisToClassifiedQuery = (
  raw: string,
  analysis: SourceAnalysis,
): ClassifiedQuery => {
  const industries = Array.isArray(analysis.industries)
    ? analysis.industries
        .map((industry) =>
          typeof industry === 'string' ? industry : industry?.name,
        )
        .filter((value): value is string => Boolean(value))
    : [];
  const seniority = Array.isArray(analysis.seniority) && analysis.seniority.length > 0
    ? analysis.seniority
    : Array.isArray(analysis.specializations)
      ? analysis.specializations
      : [];
  const validIntents = ['jobs', 'companies', 'contacts'] as const;
  const intent = validIntents.includes(analysis.intent as any)
    ? (analysis.intent as typeof validIntents[number])
    : 'companies';

  return {
    raw,
    intent,
    companies: Array.isArray(analysis.companies) ? analysis.companies : [],
    roles: Array.isArray(analysis.roles) ? analysis.roles : [],
    locations: Array.isArray(analysis.locations) ? analysis.locations : [],
    industries,
    seniority,
    similarCompanies: Array.isArray(analysis.similarCompanies) ? analysis.similarCompanies : [],
    similarTitles: Array.isArray(analysis.similarTitles) ? analysis.similarTitles : [],
    similarSkills: Array.isArray(analysis.similarSkills) ? analysis.similarSkills : [],
  };
};
